import { type ReactNode } from 'react';

import {
  DeltaInterval,
  DeltaIntervalType,
  EventConfidence,
} from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-enums';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';

export const COUNTING_LABEL: Record<DeltaIntervalType, string> = {
  [DeltaIntervalType.Calendar]: 'Calendar days',
  [DeltaIntervalType.Working]: 'Business days',
  [DeltaIntervalType.Clear]: 'Clear days',
  [DeltaIntervalType.Court]: 'Court days',
};

const CONFIDENCE_LABELS: Record<EventConfidence, string> = {
  [EventConfidence.Low]: 'Low confidence',
  [EventConfidence.Medium]: 'Medium confidence',
  [EventConfidence.High]: 'High confidence',
  [EventConfidence.Manual]: 'Manual',
};

const CONFIDENCE_CLASSES: Record<EventConfidence, string> = {
  [EventConfidence.Low]: 'bg-amber-50 text-amber-700 border-amber-200',
  [EventConfidence.Medium]: 'bg-sky-50 text-sky-700 border-sky-200',
  [EventConfidence.High]: 'bg-green-50 text-green-700 border-green-200',
  [EventConfidence.Manual]: 'bg-gray-50 text-gray-600 border-gray-200',
};

const CONFIDENCE_ICON: Record<EventConfidence, string> = {
  [EventConfidence.Low]: 'lucide--alert-triangle text-amber-600',
  [EventConfidence.Medium]: 'lucide--gauge text-sky-600',
  [EventConfidence.High]: 'lucide--shield-check text-green-600',
  [EventConfidence.Manual]: 'lucide--user text-gray-500',
};

const CONFIDENCE_TOOLTIP: Record<EventConfidence, string> = {
  [EventConfidence.Low]: 'The extractor flagged this as uncertain — verify against the source before accepting.',
  [EventConfidence.Medium]: 'The extractor is moderately confident. A quick check against the source is recommended.',
  [EventConfidence.High]: 'The extractor read this unambiguously from the source document.',
  [EventConfidence.Manual]: 'Created or edited by a user.',
};

/**
 * Shown on suggestion rows and on accepted events whose date is flagged as not
 * yet pinned down (e.g. depends on an earlier unresolved decision).
 */
export function UnconfirmedChip() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-flex">
          <Chip className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700">
            <Icon className="lucide--alert-circle text-rose-500" />
            Unconfirmed
          </Chip>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-wrap">
        This event is not yet confirmed and may change as earlier decisions in the timeline are resolved.
      </TooltipContent>
    </Tooltip>
  );
}

export function ConfidenceChip({ confidence }: { confidence: EventConfidence | null }) {
  if (confidence === null) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-flex">
          <Chip className={cn('rounded-lg border', CONFIDENCE_CLASSES[confidence])}>
            <Icon className={CONFIDENCE_ICON[confidence]} />
            {CONFIDENCE_LABELS[confidence]}
          </Chip>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-wrap">{CONFIDENCE_TOOLTIP[confidence]}</TooltipContent>
    </Tooltip>
  );
}

const COUNTING_INLINE: Record<DeltaIntervalType, string> = {
  [DeltaIntervalType.Calendar]: 'calendar',
  [DeltaIntervalType.Working]: 'business',
  [DeltaIntervalType.Clear]: 'clear',
  [DeltaIntervalType.Court]: 'court',
};

interface RelationshipUnit {
  value: number;
  singular: string;
  plural: string;
}

/**
 * Render the anchor relationship as `"Occurs 14 business days after Defence
 * due"` (or `"… before"`, or `"the same time as"` for a zero interval).
 * Returns null if we can't resolve the anchor name or there's no interval.
 */
export function formatRelationship(
  interval: DeltaInterval | null,
  type: DeltaIntervalType | null,
  anchorName: string | null,
): ReactNode | null {
  if (anchorName === null) return null;
  if (interval === null) return `Occurs at the same time as ${anchorName}`;

  const units: RelationshipUnit[] = [
    { value: interval.years ?? 0, singular: 'year', plural: 'years' },
    { value: interval.months ?? 0, singular: 'month', plural: 'months' },
    { value: interval.days ?? 0, singular: 'day', plural: 'days' },
    { value: interval.hours ?? 0, singular: 'hour', plural: 'hours' },
    { value: interval.minutes ?? 0, singular: 'minute', plural: 'minutes' },
  ];

  const nonZero = units.filter((u) => u.value !== 0);
  if (nonZero.length === 0) return `Occurs at the same time as ${anchorName}`;

  // Practical event-data assumption: all non-zero components share a sign
  // (an interval like "1 month minus 5 days" is theoretically expressible but
  // not produced by the extractor). Pick the first non-zero unit's sign.
  const direction = nonZero[0].value < 0 ? 'before' : 'after';
  const countingLabel = type !== null ? COUNTING_INLINE[type] : null;

  const segments: ReactNode[] = ['Occurs '];
  nonZero.forEach((u, idx) => {
    const abs = Math.abs(u.value);
    const word = abs === 1 ? u.singular : u.plural;
    const prefix = idx > 0 ? ', ' : '';
    if (u.singular === 'day' && countingLabel !== null) {
      segments.push(`${prefix}${abs} `);
      segments.push(
        <strong key={`b${idx}`} className="font-semibold">
          {countingLabel}
        </strong>,
      );
      segments.push(` ${word}`);
    } else {
      segments.push(`${prefix}${abs} ${word}`);
    }
  });
  segments.push(` ${direction} ${anchorName}`);

  return <span>{segments}</span>;
}
